import { useEffect, useState } from 'react';
import { useMoralisQuery } from 'react-moralis';
import useDidMountEffect from '../../utils/hooks/effects/didMount';
import { useBoolean } from '@chakra-ui/react';
import { ethers } from 'ethers';

export interface IGame {
	media: {
		coverImageUrl: string;
		logoUrl: string;
		cover?: string;
		banner: string;
		icon?: string;
	};
	colors: {
		actionColor: string;
		textColor: string;
		backgroundColor: string;
	};
	screenshots: string[];
	socials: {
		facebook: string;
		twitter: string;
		youtube: string;
		discord: string;
	};
	symbol: string;
	genres: string[];
	platforms: string[];
	color: string;
	metadata: string;
	name: string;
	address: string;
	paymentToken: string;
	price: string;
	description: string;
	gameUrl: string;
	gamepaper: string;
	video: string;
	listed: boolean;
	createdAt: string;
	updatedAt: string;
	totalSupply: string;
	creator: string;
	creatorFee: string;
	index: string;
	marketplaceFee: string;
}

export const emptyGame: IGame = {
	address: ethers.constants.AddressZero,
	color: '',
	colors: { actionColor: '', backgroundColor: '', textColor: '' },
	createdAt: '',
	description: '',
	gameUrl: '',
	gamepaper: '',
	listed: false,
	media: { banner: '', coverImageUrl: '', logoUrl: '', cover: '', icon: '' },
	name: '',
	paymentToken: '',
	metadata: '',
	price: '0',
	screenshots: [],
	socials: { discord: '', facebook: '', twitter: '', youtube: '' },
	symbol: '',
	updatedAt: '',
	video: '',
	genres: [],
	platforms: [],
	totalSupply: '',
	creator: '',
	creatorFee: '',
	index: '',
	marketplaceFee: '',
};

export enum State {
	LOADING,
	FETCHED,
}

const useGames = () => {
	const [isLoading, setIsLoading] = useBoolean(true);
	const [state, handleState] = useState(State.LOADING);
	const [games, setGames] = useState<IGame[]>([]);

	const [fetchedAddresses, setFetchedAddresses] = useState<string[]>([]);

	/** Allows for cache clearance when on an unmounted component */
	const handleSetFetchedAddresses = (addresses: typeof fetchedAddresses | null) => {
		if (!addresses) {
			setIsLoading.on();
			setGames([]);

			return;
		}

		if (!addresses.length) {
			setIsLoading.on();
			setGames([]);
			setIsLoading.off();

			return;
		}

		setFetchedAddresses(addresses);
	};

	const query = useMoralisQuery(
		'Games',
		query => {
			query.equalTo('listed', true);
			query.containedIn('address', fetchedAddresses);
			query.descending('createdAt');

			return query;
		},
		[fetchedAddresses],
		{ autoFetch: false }
	);

	useDidMountEffect(() => {
		setIsLoading.on();

		void query.fetch();
	}, [fetchedAddresses]);

	useDidMountEffect(() => {
		if (query.isLoading || query.isFetching) {
			return;
		}

		setIsLoading.off();
	}, [query.isLoading, query.isFetching]);

	useEffect(() => {
		if (isLoading) {
			handleState(State.LOADING);
		} else {
			handleState(State.FETCHED);
		}
	}, [isLoading]);

	useEffect(() => {
		const games = query.data.map(game => game.attributes as IGame);
		const promoted = games.find(game => game.address === '0xbac85939aed35d518283cc73d4f303cfd35d8ff0');
		if (promoted !== null && promoted !== undefined) {
			games.splice(
				games.findIndex(game => game.address === promoted.address),
				1
			);
			setGames([promoted, ...games]);
		} else setGames(games);
	}, [query.data]);

	return { state, data: games, fetch: handleSetFetchedAddresses };
};

export default useGames;
